import {
  MouseEventHandler,
  TouchEventHandler,
  useState,
  TouchEvent,
  Touch,
  MouseEvent
} from 'react';
import { HtmlVector } from 'record-it-utils/misc/HtmlVector/HtmlVector';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useEventListener } from '../../hooks/useEventListener';
import { CalculatedTick } from './useCalculatedTickConfiguration';

export function useEventHandlers({
  value,
  allTicks,
  container,
  onValueChanged,
  onValueSelected
}: UseEventHandlersOptions): UseEventHandlersReturnValue {
  const [highlightedValueOverride, setHighlightedValueOverride] = useState<
    number | null
  >(null);

  const onMouseDown: MouseEventHandler<HTMLElement> = useEventCallback(
    (event) => {
      if (event.buttons === 1) {
        // only do something if mouse button 1 is pressed
        updateHighlightedValueOverride(event);
      }
    }
  );

  const onMouseMove: MouseEventHandler<HTMLElement> = useEventCallback(
    (event) => {
      if (event.buttons === 1) {
        // only do something if mouse button 1 is pressed
        updateHighlightedValueOverride(event);
      }
    }
  );

  useEventListener(document.body, 'mouseup', () => {
    acceptHighlightedValueOveride();
  });

  const onTouchStart: TouchEventHandler<HTMLElement> = useEventCallback(
    (event) => {
      const touch = getSingleTouch(event);
      if (touch) {
        updateHighlightedValueOverride(touch);
      } else {
        setHighlightedValueOverride(null);
      }
    }
  );

  const onTouchMove: TouchEventHandler<HTMLElement> = useEventCallback(
    (event) => {
      const touch = getSingleTouch(event);
      if (touch) {
        updateHighlightedValueOverride(touch);
      }
    }
  );

  const onTouchEnd: TouchEventHandler<HTMLElement> = useEventCallback(
    (event) => {
      if (event.touches.length === 0) {
        event.preventDefault();
        acceptHighlightedValueOveride();
      }
    }
  );

  const onTouchCancel: TouchEventHandler<HTMLElement> = useEventCallback(() => {
    setHighlightedValueOverride(null);
  });

  function getSingleTouch(event: TouchEvent<HTMLElement>): Touch | null {
    const firstTouch = event.touches[0];
    if (firstTouch && event.touches.length === 1) {
      return firstTouch;
    }

    return null;
  }

  function updateHighlightedValueOverride(
    eventOrTouch: MouseEvent | Touch
  ): void {
    const position = getRelativePosition(
      eventOrTouch.clientX,
      eventOrTouch.clientY
    );
    const closestTick = findClosestSelectableTick(position);
    if (closestTick) {
      setHighlightedValueOverride(closestTick.value);
    }
  }

  function acceptHighlightedValueOveride(): void {
    if (highlightedValueOverride != null) {
      onValueSelected?.(highlightedValueOverride);

      if (highlightedValueOverride !== value) {
        onValueChanged?.(highlightedValueOverride);
      }
      setHighlightedValueOverride(null);
    }
  }

  function getRelativePosition(clientX: number, clientY: number): HtmlVector {
    assertNotNullOrUndefined(
      container,
      "can't calculate a position without a container"
    );
    const boundingRect = container.getBoundingClientRect();
    const topLeftPosition = new HtmlVector(boundingRect.left, boundingRect.top);

    return new HtmlVector(clientX, clientY).substractVector(topLeftPosition);
  }

  function findClosestSelectableTick(
    position: HtmlVector
  ): CalculatedTick | null {
    const threshold = 36;

    let closestTick: CalculatedTick | null = null;
    let closestDistance: number | null = null;

    const selectableTicks = allTicks.filter((t) => t.selectable);

    for (const tick of selectableTicks) {
      const diffVector = position.substractVector(tick.position);
      const distance = diffVector.getLength();

      if (
        distance < threshold &&
        (closestDistance == null || distance < closestDistance)
      ) {
        closestTick = tick;
        closestDistance = distance;
      }
    }

    return closestTick;
  }

  const valueToHighlight = highlightedValueOverride ?? value;

  return {
    highlightedTick: allTicks.find((t) => t.value === valueToHighlight) ?? null,
    containerProps: {
      onMouseMove,
      onMouseDown,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onTouchCancel
    }
  };
}

export type UseEventHandlersOptions = {
  container: HTMLElement | null;
  value: number;
  onValueChanged: ((value: number) => void) | undefined;
  onValueSelected: ((value: number) => void) | undefined;
  allTicks: Array<CalculatedTick>;
};

export type UseEventHandlersReturnValue = {
  highlightedTick: CalculatedTick | null;
  containerProps: {
    onMouseDown: MouseEventHandler<HTMLElement>;
    onMouseMove: MouseEventHandler<HTMLElement>;
    onTouchStart: TouchEventHandler<HTMLElement>;
    onTouchMove: TouchEventHandler<HTMLElement>;
    onTouchEnd: TouchEventHandler<HTMLElement>;
    onTouchCancel: TouchEventHandler<HTMLElement>;
  };
};
